import { FC, useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import type { Feature } from "@services/permissions/commands/cmdCreateFeature";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (data: { name: string; parent_id: number | null }) => void;
  feature: Feature | null;
  allFeatures: Feature[];
  isSaving: boolean;
}

const FeatureFormModal: FC<Props> = ({ show, onClose, onSave, feature, allFeatures, isSaving }) => {
  const isEdit = feature !== null;
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState<string>("");

  useEffect(() => {
    if (show) {
      setName(feature?.name || "");
      setParentId(feature?.parent_id != null ? String(feature.parent_id) : "");
    }
  }, [show, feature]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), parent_id: parentId ? Number(parentId) : null });
  };

  // Exclude self and own children from parent options to prevent cycles
  const parentOptions = allFeatures.filter((f) => !feature || f.id !== feature.id);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{isEdit ? "Edit Feature" : "New Feature"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Engagements"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label className="fw-semibold">Parent Feature</Form.Label>
          <Form.Select value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">None (root level)</option>
            {parentOptions.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Nest this feature under a parent to create a hierarchy.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !name.trim()}>
          {isSaving ? (
            <><span className="spinner-border spinner-border-sm me-2" /> Saving...</>
          ) : (
            <><i className="bi bi-check-circle me-2" />{isEdit ? "Save Changes" : "Create Feature"}</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FeatureFormModal;
